import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  PlayIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  BookmarkIcon,
  TagIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const ExamCreate = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const subjectId = searchParams.get('subject');
  const mode = searchParams.get('mode') || 'practice';

  const [subject, setSubject] = useState(null);
  const [testParameters, setTestParameters] = useState(null);
  const [subjectStructure, setSubjectStructure] = useState(null);
  const [examConfig, setExamConfig] = useState({
    mode: mode,
    title: '',
    total_questions: 20,
    time_limit_minutes: null,
    passing_score: 75,
    selected_chapters: [],
    selected_topics: [],
    include_incorrect_only: false,
    include_unseen_only: false,
    include_marked_easy: false,
    include_marked_difficult: false,
    include_pinned_only: false,
    tags_filter: []
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (subjectId) {
      fetchSubjectData();
      fetchTestParameters();
      fetchSubjectStructure();
    }
  }, [subjectId]);

  useEffect(() => {
    if (mode === 'real_test' && testParameters) {
      setExamConfig(prev => ({
        ...prev,
        total_questions: testParameters.total_questions,
        time_limit_minutes: testParameters.time_limit_minutes,
        passing_score: testParameters.passing_score,
        title: `${subject?.name} - Real Test`
      }));
    } else if (mode === 'practice' && subject) {
      setExamConfig(prev => ({
        ...prev,
        title: `${subject?.name} - Practice`
      }));
    }
  }, [mode, testParameters, subject]);

  const fetchSubjectData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/courses/subjects/${subjectId}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubject(data);
      }
    } catch (error) {
      // Error fetching subject
    }
  };

  const fetchTestParameters = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exams/subjects/${subjectId}/test-parameters/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTestParameters(data);
      }
    } catch (error) {
      // Error fetching test parameters
    }
  };

  const fetchSubjectStructure = async () => {
    try {
      const response = await fetch(`${API_URL}/api/courses/subjects/${subjectId}/structure/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubjectStructure(data);
      }
    } catch (error) {
      // Error fetching subject structure
    } finally {
      setLoading(false);
    }
  };

  const handleChapterToggle = (chapterId) => {
    const chapter = subjectStructure.chapters.find(c => c.id === chapterId);
    const isSelected = examConfig.selected_chapters.includes(chapterId);
    
    if (isSelected) {
      // Remove chapter and its topics
      setExamConfig(prev => ({
        ...prev,
        selected_chapters: prev.selected_chapters.filter(id => id !== chapterId),
        selected_topics: prev.selected_topics.filter(topicId => 
          !chapter.topics.some(topic => topic.id === topicId)
        )
      }));
    } else {
      // Add chapter and all its topics
      const topicIds = chapter.topics.map(topic => topic.id);
      setExamConfig(prev => ({
        ...prev,
        selected_chapters: [...prev.selected_chapters, chapterId],
        selected_topics: [...new Set([...prev.selected_topics, ...topicIds])]
      }));
    }
  };

  const handleTopicToggle = (topicId, chapterId) => {
    const isSelected = examConfig.selected_topics.includes(topicId);
    const chapter = subjectStructure.chapters.find(c => c.id === chapterId);
    
    if (isSelected) {
      // Remove topic
      const newSelectedTopics = examConfig.selected_topics.filter(id => id !== topicId);
      const chapterTopicsSelected = chapter.topics.filter(topic => 
        newSelectedTopics.includes(topic.id)
      ).length;
      
      setExamConfig(prev => ({
        ...prev,
        selected_topics: newSelectedTopics,
        selected_chapters: chapterTopicsSelected === 0 
          ? prev.selected_chapters.filter(id => id !== chapterId)
          : prev.selected_chapters
      }));
    } else {
      // Add topic
      const newSelectedTopics = [...examConfig.selected_topics, topicId];
      const chapterTopicsSelected = chapter.topics.filter(topic => 
        newSelectedTopics.includes(topic.id)
      ).length;
      
      setExamConfig(prev => ({
        ...prev,
        selected_topics: newSelectedTopics,
        selected_chapters: chapterTopicsSelected === chapter.topics.length
          ? [...new Set([...prev.selected_chapters, chapterId])]
          : prev.selected_chapters.filter(id => id !== chapterId)
      }));
    }
  };

  const handleSelectAll = () => {
    const allChapterIds = subjectStructure.chapters.map(c => c.id);
    const allTopicIds = subjectStructure.chapters.flatMap(c => c.topics.map(t => t.id));
    
    setExamConfig(prev => ({
      ...prev,
      selected_chapters: allChapterIds,
      selected_topics: allTopicIds
    }));
  };

  const handleDeselectAll = () => {
    setExamConfig(prev => ({
      ...prev,
      selected_chapters: [],
      selected_topics: []
    }));
  };

  const calculateAvailableQuestions = () => {
    if (!subjectStructure) return 0;
    
    let total = 0;
    subjectStructure.chapters.forEach(chapter => {
      if (examConfig.selected_chapters.includes(chapter.id)) {
        total += chapter.total_questions;
      } else {
        chapter.topics.forEach(topic => {
          if (examConfig.selected_topics.includes(topic.id)) {
            total += topic.total_questions;
          }
        });
      }
    });
    
    return total;
  };

  const handleCreateExam = async () => {
    setCreating(true);
    
    try {
      const response = await fetch(`${API_URL}/api/exams/sessions/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
        },
        body: JSON.stringify({
          ...examConfig,
          subject: subjectId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        navigate(`/exam/${data.exam_session_id}`);
      } else {
        const error = await response.json();
        alert(`Error creating exam: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Error creating exam. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const availableQuestions = calculateAvailableQuestions();
  const isRealTest = mode === 'real_test';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Create {isRealTest ? 'Real Test' : 'Practice Exam'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {subject?.code} - {subject?.name}
            </p>
          </div>
          <div className={`px-4 py-2 rounded-lg ${
            isRealTest 
              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
          }`}>
            {isRealTest ? 'Real Test Mode' : 'Practice Mode'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Exam Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Exam Configuration
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Exam Title
                </label>
                <input
                  type="text"
                  value={examConfig.title}
                  onChange={(e) => setExamConfig(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={isRealTest}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Number of Questions
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={availableQuestions}
                    value={examConfig.total_questions}
                    onChange={(e) => setExamConfig(prev => ({ 
                      ...prev, 
                      total_questions: parseInt(e.target.value) || 1 
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={isRealTest}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Time Limit (minutes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={examConfig.time_limit_minutes || ''}
                    onChange={(e) => setExamConfig(prev => ({ 
                      ...prev, 
                      time_limit_minutes: e.target.value ? parseInt(e.target.value) : null 
                    }))}
                    placeholder="No limit"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={isRealTest}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Passing Score (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={examConfig.passing_score}
                    onChange={(e) => setExamConfig(prev => ({ 
                      ...prev, 
                      passing_score: parseInt(e.target.value) || 75 
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={isRealTest}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Question Filters */}
          {!isRealTest && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Question Filters
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FilterCheckbox
                  label="Incorrect Questions Only"
                  description="Only questions you've answered incorrectly"
                  checked={examConfig.include_incorrect_only}
                  onChange={(checked) => setExamConfig(prev => ({ 
                    ...prev, 
                    include_incorrect_only: checked 
                  }))}
                  icon={XCircleIcon}
                />
                
                <FilterCheckbox
                  label="Unseen Questions Only"
                  description="Only questions you haven't attempted"
                  checked={examConfig.include_unseen_only}
                  onChange={(checked) => setExamConfig(prev => ({ 
                    ...prev, 
                    include_unseen_only: checked 
                  }))}
                  icon={EyeIcon}
                />
                
                <FilterCheckbox
                  label="Marked Easy"
                  description="Questions you've marked as easy"
                  checked={examConfig.include_marked_easy}
                  onChange={(checked) => setExamConfig(prev => ({ 
                    ...prev, 
                    include_marked_easy: checked 
                  }))}
                  icon={CheckCircleIcon}
                />
                
                <FilterCheckbox
                  label="Marked Difficult"
                  description="Questions you've marked as difficult"
                  checked={examConfig.include_marked_difficult}
                  onChange={(checked) => setExamConfig(prev => ({ 
                    ...prev, 
                    include_marked_difficult: checked 
                  }))}
                  icon={XCircleIcon}
                />
                
                <FilterCheckbox
                  label="Pinned Questions"
                  description="Questions you've pinned for review"
                  checked={examConfig.include_pinned_only}
                  onChange={(checked) => setExamConfig(prev => ({ 
                    ...prev, 
                    include_pinned_only: checked 
                  }))}
                  icon={BookmarkIcon}
                />
              </div>
            </div>
          )}

          {/* Chapter/Topic Selection */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Chapter & Topic Selection
              </h2>
              <div className="space-x-2">
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  disabled={isRealTest}
                >
                  Select All
                </button>
                <button
                  onClick={handleDeselectAll}
                  className="text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400"
                  disabled={isRealTest}
                >
                  Deselect All
                </button>
              </div>
            </div>

            {isRealTest && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Real test mode includes all chapters and topics automatically.
                </p>
              </div>
            )}

            <div className="space-y-2">
              {subjectStructure?.chapters.map((chapter) => (
                <ChapterSelector
                  key={chapter.id}
                  chapter={chapter}
                  selectedChapters={examConfig.selected_chapters}
                  selectedTopics={examConfig.selected_topics}
                  onChapterToggle={handleChapterToggle}
                  onTopicToggle={handleTopicToggle}
                  disabled={isRealTest}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Summary Panel */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Exam Summary
            </h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Mode:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {isRealTest ? 'Real Test' : 'Practice'}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Questions:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {examConfig.total_questions}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Available:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {availableQuestions}
                </span>
              </div>
              
              {examConfig.time_limit_minutes && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Time Limit:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {examConfig.time_limit_minutes} min
                  </span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Passing Score:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {examConfig.passing_score}%
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Chapters:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {isRealTest ? 'All' : examConfig.selected_chapters.length}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Topics:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {isRealTest ? 'All' : examConfig.selected_topics.length}
                </span>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCreateExam}
                disabled={creating || availableQuestions < examConfig.total_questions}
                className={`w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white transition-colors ${
                  creating || availableQuestions < examConfig.total_questions
                    ? 'bg-gray-400 cursor-not-allowed'
                    : isRealTest
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Start Exam
                  </>
                )}
              </button>
              
              {availableQuestions < examConfig.total_questions && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400 text-center">
                  Not enough questions available. Select more chapters/topics.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterCheckbox = ({ label, description, checked, onChange, icon: Icon }) => {
  return (
    <div className="flex items-start space-x-3">
      <div className="flex items-center h-5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
      </div>
      <div className="flex-1">
        <div className="flex items-center">
          <Icon className="h-4 w-4 text-gray-400 mr-2" />
          <label className="text-sm font-medium text-gray-900 dark:text-white">
            {label}
          </label>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {description}
        </p>
      </div>
    </div>
  );
};

const ChapterSelector = ({ 
  chapter, 
  selectedChapters, 
  selectedTopics, 
  onChapterToggle, 
  onTopicToggle, 
  disabled 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isChapterSelected = selectedChapters.includes(chapter.id);
  const selectedTopicsInChapter = chapter.topics.filter(topic => 
    selectedTopics.includes(topic.id)
  ).length;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isChapterSelected}
            onChange={() => onChapterToggle(chapter.id)}
            disabled={disabled}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              {chapter.name}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {chapter.total_questions} questions
              {selectedTopicsInChapter > 0 && !isChapterSelected && (
                <span className="ml-2 text-blue-600 dark:text-blue-400">
                  ({selectedTopicsInChapter} topics selected)
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{ 
                width: `${chapter.attempted_questions > 0 
                  ? (chapter.correct_answers / chapter.attempted_questions) * 100 
                  : 0}%` 
              }}
            ></div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-700/50">
          <div className="space-y-2">
            {chapter.topics.map((topic) => (
              <div key={topic.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedTopics.includes(topic.id)}
                    onChange={() => onTopicToggle(topic.id, chapter.id)}
                    disabled={disabled || isChapterSelected}
                    className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm text-gray-900 dark:text-white">
                      {topic.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {topic.total_questions} questions
                    </div>
                  </div>
                </div>
                
                <div className="w-20 bg-gray-200 dark:bg-gray-600 rounded-full h-1">
                  <div
                    className="bg-green-500 h-1 rounded-full"
                    style={{ 
                      width: `${topic.attempted_questions > 0 
                        ? (topic.correct_answers / topic.attempted_questions) * 100 
                        : 0}%` 
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamCreate;
